'use client';
import React, { FC, useState } from 'react';
import dayjs from 'dayjs';
import { TypeNewsletterBlockFields } from '@/core/generated-types';
import { DynamicComponent } from '@/components/common/DynamicComponent';
import { pushToDataLayer } from '@/utils/dataLayer';
import { Button } from '@/components/ui';
import Heading, { HeadingTag } from '@/components/ui/Heading/Heading';
import { ChildBirthDateInput } from '@/utils/dates/constants';
import { track } from '@/utils/rudderstack';
import { normalizeAndFormatBirthDateToString } from '@/utils/dates/dateFormatters';

const NewsletterBlock: FC<TypeNewsletterBlockFields> = ({
  entryName,
  title,
  text,
  success,
  childBirthDateInput,
  emailInput,
  submitButton,
  anchorId,
  backgroundColor,
}) => {
  const [childBirthDate, setChildBirthDate] = useState<ChildBirthDateInput>({
    year: '',
    month: '',
    day: '',
  });
  const [childBirthDateError, setChildBirthDateError] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const updateChildBirthDate = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: string,
  ) => {
    setChildBirthDate((prevState) => ({
      ...prevState,
      [type]: event.target.value,
    }));

    if (successMessage) {
      setSuccessMessage('');
    }
  };

  const validateChildBirthDate = (target: any, date: Date) => {
    // No freeform date field.
    if (!childBirthDateInput) {
      return '';
    }

    // No data was entered.
    if (!childBirthDate.year && !childBirthDate.month && !childBirthDate.day) {
      if (childBirthDateInput.fields.required) {
        return childBirthDateInput.fields.requiredError || '';
      } else {
        return '';
      }
    }

    // At least one date fragment is empty.
    if (!childBirthDate.year || !childBirthDate.month || !childBirthDate.day) {
      return childBirthDateInput.fields.incompleteDateError;
    }

    const yearId = `${childBirthDateInput.fields.id}-year`;
    const monthId = `${childBirthDateInput.fields.id}-month`;
    const dayId = `${childBirthDateInput.fields.id}-day`;

    const yearIsValid = target[yearId].checkValidity();
    const monthIsValid = target[monthId].checkValidity();
    const dayIsValid = target[dayId].checkValidity();

    // Input is not a plausible date based on provided patterns (e.g., February 29 on a non-leap year, April 31, month=13, or day=90, or year=870).
    if (!yearIsValid || !monthIsValid || !dayIsValid || isNaN(date.getTime())) {
      return childBirthDateInput.fields.invalidDateError;
    }

    return '';
  };

  const updateEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);

    if (successMessage) {
      setSuccessMessage('');
    }
  };

  const validateEmail = (target: any) => {
    if (emailInput.fields.required && !email) {
      return emailInput.fields.requiredError || '';
    }

    if (target && !target.checkValidity()) {
      return emailInput.fields.validationError || '';
    }

    return '';
  };

  const submitNewsletterSignUp = (event: any) => {
    event.preventDefault();

    if (successMessage) {
      setSuccessMessage('');
    }

    const normalizedDateString =
      normalizeAndFormatBirthDateToString(childBirthDate);
    const birthDate = dayjs(normalizedDateString, 'YYYY-MM-DD', true);

    const nativeDate = birthDate.toDate();
    const childBirthDateError = validateChildBirthDate(
      event.target,
      nativeDate,
    );

    const emailError = validateEmail(
      emailInput.fields.id && event.target[emailInput.fields.id],
    );

    setChildBirthDateError(childBirthDateError);
    setEmailError(emailError);

    if (emailError || childBirthDateError) {
      return;
    }

    let formattedDate = '';
    if (birthDate.isValid()) {
      formattedDate = birthDate.format('YYYY-MM-DD');
    }

    // See for reference: https://github.com/lovevery-digital/Lovevery/blob/fa31b5a5ceabd1c91058778b5b1ea93eecefa30c/src/scripts/shared/components/NewsletterSignup/NewsletterSignup.tsx#L58
    const signUpEvent = {
      event: 'newsletterSignup',
      newsLetterEmail: email,
      child1_birth_date: formattedDate,
      emailPreferences:
        "What's new with my baby’s development (stage-based development information), What's new from Lovevery (promotions, sales and product launches)",
      source: 'Main Site - Footer',
      optin_email_promotions: true,
      optin_email_developmental: true,
    };

    // This does not work if not on a Lovevery domain name.
    pushToDataLayer(signUpEvent);
    track('newsletter_signup', {
      email: email,
      child_birth_date: formattedDate,
    });
    setSuccessMessage(success);
    setEmail('');
    setChildBirthDate({ year: '', month: '', day: '' });
  };

  return (
    <section
      data-testid={`newsletter-block-${entryName}`}
      id={anchorId}
      className={`bg-${backgroundColor?.fields?.color}`}
    >
      <div className="mx-auto flex max-w-[1000px] flex-col items-start px-6 py-10 md:flex-row md:py-20">
        <div className="md:w-1/2 md:pr-12">
          <Heading tag={HeadingTag.H2} text={title} />
          <p className="mb-8 mt-4 text-base md:mb-0 md:text-lg">{text}</p>
        </div>
        {/* We use noValidate since we're handling validation issues ourselves but still take advantage of built-in behaviors like pattern, minlength, and event.target.checkValidity(). */}
        <form
          onSubmit={submitNewsletterSignUp}
          noValidate
          className="w-full md:w-1/2 md:pl-12"
        >
          <div className="mb-3">
            <DynamicComponent
              component={childBirthDateInput}
              value={childBirthDate}
              error={childBirthDateError}
              onChange={updateChildBirthDate}
            />
          </div>
          <div className="mb-6">
            <DynamicComponent
              component={emailInput}
              value={email}
              error={emailError}
              onChange={updateEmail}
            />
          </div>
          <Button
            type="submit"
            ctaText={submitButton.fields.ctaText}
            classes="w-full md:w-auto btn-responsive"
          />
          {successMessage && (
            <div className="my-4 text-sm text-success">{successMessage}</div>
          )}
        </form>
      </div>
    </section>
  );
};

export default React.memo(NewsletterBlock);
